import axios from 'axios';
import { STORE_CONTEXT_STORAGE_KEY, useStoreContextStore } from '../store/storeContextStore';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const PRODUCTS_CACHE_TTL_MS = Number(process.env.REACT_APP_PRODUCTS_CACHE_TTL_MS || 2 * 60 * 1000);
const productsRequestCache = new Map();
const productsInFlightRequests = new Map();

const readSelectedStoreContext = () => {
  const selectedStoreFromState = useStoreContextStore.getState?.().selectedStore;
  if (selectedStoreFromState && typeof selectedStoreFromState === 'object') {
    const context = {
      storeInfoId: selectedStoreFromState.storeInfoId || selectedStoreFromState.store_info_id,
      vendorId: selectedStoreFromState.vendorId || selectedStoreFromState.vendor_id,
      legacyStoreId: selectedStoreFromState.legacyStoreId || selectedStoreFromState.legacy_store_id,
    };

    if (context.storeInfoId || context.vendorId || context.legacyStoreId) {
      return context;
    }
  }

  try {
    const raw = localStorage.getItem(STORE_CONTEXT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const selectedStore = parsed?.state?.selectedStore;
    if (!selectedStore || typeof selectedStore !== 'object') return null;
    return {
      storeInfoId: selectedStore.storeInfoId || selectedStore.store_info_id,
      vendorId: selectedStore.vendorId || selectedStore.vendor_id,
      legacyStoreId: selectedStore.legacyStoreId || selectedStore.legacy_store_id,
    };
  } catch (_) {
    return null;
  }
};

const withSelectedStoreParams = (params = {}) => {
  const storeContext = readSelectedStoreContext();
  return {
    ...(params || {}),
    ...(params?.storeInfoId || params?.store_info_id || !storeContext?.storeInfoId ? {} : { storeInfoId: storeContext.storeInfoId }),
    ...(params?.vendorId || params?.vendor_id || !storeContext?.vendorId ? {} : { vendorId: storeContext.vendorId }),
    ...(params?.storeId || params?.store_id || !storeContext?.legacyStoreId ? {} : { storeId: storeContext.legacyStoreId }),
  };
};

const buildProductsCacheKey = (params = {}) => {
  const entries = Object.entries(params || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([left], [right]) => String(left).localeCompare(String(right)));
  return JSON.stringify(entries);
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const storeContext = readSelectedStoreContext();
    if (storeContext?.storeInfoId) {
      config.headers['X-OneMart-Store-Info-Id'] = String(storeContext.storeInfoId);
    }
    if (storeContext?.vendorId) {
      config.headers['X-OneMart-Vendor-Id'] = String(storeContext.vendorId);
    }
    if (storeContext?.legacyStoreId) {
      config.headers['X-OneMart-Legacy-Store-Id'] = String(storeContext.legacyStoreId);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 without redirect loop
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect if we're not already on the login page
    if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem(STORE_CONTEXT_STORAGE_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  requestPasswordReset: (data) => api.post('/auth/request-password-reset', data),
  getPasswordResetRequestStatus: (email) => api.get('/auth/request-password-reset-status', { params: { email } }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (data) => api.put('/auth/password', data),
  resetPassword: (data) => api.put('/auth/reset-password', data),
  initRoles: () => api.post('/auth/init-roles'),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  searchCandidates: (params) => api.get('/users/candidates', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getRoles: () => api.get('/users/roles'),
  createRole: (data) => api.post('/users/roles', data),
  getDesignations: () => api.get('/users/designations'),
  updateRole: (id, roleId) => api.put(`/users/${id}/role`, { roleId }),
  updatePassword: (id, data) => api.put(`/users/${id}/password`, data),
};

// Stores API
export const storesAPI = {
  getAll: (params) => api.get('/stores', { params }),
  getContext: (params) => api.get('/stores/context', { params }),
  updateContext: (data) => api.put('/stores/context', data),
  create: (data) => api.post('/stores', data),
};

export const assetsAPI = {
  getTemperature: (params) => api.get('/assets/temperature', { params }),
  getAll: (params) => api.get('/assets', { params }),
  save: (data) => api.post('/assets', data),
  deactivate: (id) => api.delete(`/assets/${id}`),
};

export const productsAPI = {
  getAll: (params) => api.get('/products', { params: withSelectedStoreParams(params) }),
  getClassified: (params) => api.get('/products/classified', { params: withSelectedStoreParams(params) }),
  getAllCached: async (params = {}, options = {}) => {
    const { forceRefresh = false } = options;
    const scopedParams = withSelectedStoreParams(params);
    const cacheKey = buildProductsCacheKey(scopedParams);
    const now = Date.now();

    if (!forceRefresh) {
      const cachedEntry = productsRequestCache.get(cacheKey);
      if (cachedEntry && (now - cachedEntry.ts) < PRODUCTS_CACHE_TTL_MS) {
        return cachedEntry.response;
      }

      const inFlight = productsInFlightRequests.get(cacheKey);
      if (inFlight) {
        return inFlight;
      }
    }

    const requestPromise = api.get('/products', { params: scopedParams })
      .then((response) => {
        productsRequestCache.set(cacheKey, { response, ts: Date.now() });
        return response;
      })
      .finally(() => {
        productsInFlightRequests.delete(cacheKey);
      });

    productsInFlightRequests.set(cacheKey, requestPromise);
    return requestPromise;
  },
  clearGetAllCache: () => {
    productsRequestCache.clear();
    productsInFlightRequests.clear();
  },
  getSellers: (params) => api.get('/products/sellers', { params: withSelectedStoreParams(params) }),
  getTaskItems: (params) => api.get('/products/task-items', { params: withSelectedStoreParams(params) }),
  getSyncStatus: () => api.get('/products/sync-status'),
  sync: () => api.post('/products/sync'),
  syncAuditSnapshot: (data) => api.post('/products/audit-snapshot', data),
  updateSyncSettings: (data) => api.put('/products/sync-settings', data),
};

export const logisticsOrdersAPI = {
  getAll: (params) => api.get('/logistics-orders', { params }),
  create: (data) => api.post('/logistics-orders', data),
  update: (id, data) => api.put(`/logistics-orders/${id}`, data),
  delete: (id) => api.delete(`/logistics-orders/${id}`),
  requestUnlock: (id) => api.put(`/logistics-orders/${id}`, { requestUnlock: true }),
  approveUnlock: (id) => api.put(`/logistics-orders/${id}`, { approveUnlock: true }),
  getGoodsReceived: (params) => api.get('/logistics-orders/goods-received', { params }),
  updateGoodsReceived: (id, data) => api.put(`/logistics-orders/${id}/goods-received`, data),
  getGoodsReceivedReport: (params) => api.get('/logistics-orders/goods-received-report', { params }),
};

export const dairyOrdersAPI = {
  getDraft: (params) => api.get('/dairy-orders/draft', { params }),
  saveDraft: (data) => api.post('/dairy-orders/draft', data),
  deleteDraft: (id) => api.delete(`/dairy-orders/draft/${id}`),
  getMine: (params) => api.get('/dairy-orders', { params }),
  create: (data) => api.post('/dairy-orders', data),
  getReport: (params) => api.get('/dairy-orders/report', { params }),
  approveEditFromReport: (data) => api.post('/dairy-orders/report/approve-edit', data),
  rejectEditFromReport: (data) => api.post('/dairy-orders/report/reject-edit', data),
};

export const productReturnsAPI = {
  getAll: (params) => api.get('/product-returns', { params }),
  save: (data) => api.post('/product-returns', data),
  delete: (id) => api.delete(`/product-returns/${id}`),
  getReport: (params) => api.get('/product-returns/report', { params }),
  approveEditFromReport: (data) => api.post('/product-returns/report/approve-edit', data),
  rejectEditFromReport: (data) => api.post('/product-returns/report/reject-edit', data),
};

export const produceTrackerAPI = {
  getAll: (params) => api.get('/produce-tracker', { params }),
  getReport: (params) => api.get('/produce-tracker/report', { params }),
  save: (data) => api.post('/produce-tracker', data),
};

export const itemChangeLogAPI = {
  getAll: (params) => api.get('/item-change-logs', { params }),
  getOptions: () => api.get('/item-change-logs/options'),
  create: (data) => api.post('/item-change-logs', data),
  update: (id, data) => api.put(`/item-change-logs/${id}`, data),
  bulkUpdate: (data) => api.post('/item-change-logs/bulk-update', data),
  getAudit: (id) => api.get(`/item-change-logs/${id}/audit`),
};

// Inventory API
export const inventoryAPI = {
  getCountReport: (params) => api.get('/inventory/count-report', { params }),
  getFullCountSchedules: (params) => api.get('/inventory/full-count-schedules', { params }),
  saveFullCountSchedule: (data) => api.post('/inventory/full-count-schedules', data),
  getFullCountTask: (params) => api.get('/inventory/full-count-task', { params }),
  saveFullCountTask: (data) => api.post('/inventory/full-count-task', data),
  getFullCountReport: (params) => api.get('/inventory/full-count-report', { params }),
  getAlerts: (params) => api.get('/inventory/alerts/summary', { params }),
};

// Reports API
export const reportsAPI = {
  getAll: (params) => api.get('/reports', { params }),
  getFinalReport: (params) => api.get('/reports/final-report', { params }),
  getFinalReportTrend: (params) => api.get('/reports/final-report/trend', { params }),
  getById: (id) => api.get(`/reports/${id}`),
  generate: (data) => api.post('/reports/generate', data),
  delete: (id) => api.delete(`/reports/${id}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getActivities: () => api.get('/dashboard/activities'),
  getUpcomingTasks: () => api.get('/dashboard/upcoming-tasks'),
  getPerformance: (days) => api.get('/dashboard/performance', { params: { days } }),
  getAdminOverview: () => api.get('/dashboard/admin-overview'),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getBoardSummary: () => api.get('/notifications/board-summary'),
  broadcast: (data) => api.post('/notifications/broadcast', data),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  getPreferences: () => api.get('/notifications/preferences'),
};

// Admin API
export const adminAPI = {
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  getSystemStats: () => api.get('/admin/system-stats'),
  getRoles: () => api.get('/admin/roles'),
  updateRolePermissions: (id, permissions) => api.put(`/admin/roles/${id}/permissions`, { permissions }),
  updateUserFeatureAccess: (id, payload) => api.put(`/admin/users/${id}/feature-access`, payload),
  getCategoryClassifications: (params) => api.get('/admin/category-classifications', { params }),
  searchCategoryClassificationCategories: (params) => api.get('/admin/category-classifications/categories', { params }),
  saveCategoryClassification: (data) => api.post('/admin/category-classifications', data),
  deleteCategoryClassification: (id) => api.delete(`/admin/category-classifications/${id}`),
  bulkUserOperations: (data) => api.post('/admin/users/bulk', data),
};

// Compliance Tasks API
export const complianceTasksAPI = {
  logTask: (data) => api.post('/compliance-tasks/log', data),
  getLogs: (params) => api.get('/compliance-tasks/logs', { params }),
  getSummary: (params) => api.get('/compliance-tasks/summary', { params }),
  getWorkspace: (params) => api.get('/compliance-tasks/workspace', { params }),
  getDayBoard: (params) => api.get('/compliance-tasks/day-board', { params }),
};

export const workforceRosterAPI = {
  get: (params) => api.get('/workforce-roster', { params }),
  getUsers: (params) => api.get('/workforce-roster/users', { params }),
  sync: (data) => api.post('/workforce-roster/sync', data),
};

export default api;
